import {
	parseTemplateStringIntoSegments,
	TemplateSegmentType,
} from '@mission.io/question-toolkit'
import React, { ReactNode, useContext } from 'react'
import { getMarkdownString } from '../../components/Markdown/hooks'
import { MdxJsxFlowElement, MdxJsxTextElement } from 'mdast-util-mdx-jsx'

export type ClozeDropdownState = Record<string, ReactNode>
export type ClozeDropdownProviderState = {
	dropDowns: ClozeDropdownState
}

export const COMPONENT_NAME = 'TemplateDropdownButton'

const ClozeButtonContext = React.createContext<ClozeDropdownProviderState>({
	dropDowns: {},
})

/**
 * ClozeDropdownProvider - a context which contains the components to render for the dropdowns in the clozeDropdown question type
 *
 * @param {Object} props - the react props
 * @param {ClozeDropdownProviderState} props.state - contains an object mapping the templateResponseKey to the component to render when it is encountered
 * @param {JSX.Element} children - the cloze dropdown rendering component
 *
 * @return {JSX.Element}
 */
export function ClozeDropdownProvider({
	state,
	children,
}: {
	state: ClozeDropdownProviderState
	children: JSX.Element
}): JSX.Element {
	return (
		<ClozeButtonContext.Provider value={state}>
			{children}
		</ClozeButtonContext.Provider>
	)
}

/**
 * useDropdownData - get the component to render for the specified dropdownId, null if no such component could be found.
 *
 * @param {string | null  |undefined} dropdownId - the id of the dropdown to get the component for
 *
 * @return {JSX.Element | null}
 */
export function useDropdownData(dropdownId: unknown): ReactNode {
	const providerContext = useContext(ClozeButtonContext)
	if (typeof dropdownId !== 'string' || !providerContext) {
		return null
	}
	return providerContext.dropDowns[dropdownId] ?? null
}

/**
 * PdfMarkdownSelectComponent - a component used to render the correct button for the given dropdownId as described in the `ClozeDropdownProvider`.
 * Will be null if unable to find the component to render in the `ClozeDropdownProvider` state.
 *
 * @param {Object} props - the react props
 * @param {MdxJsxFlowElement | MdxJsxTextElement} props.node - the markdown html node containing the id of the dropdown button to render
 *
 * @return {JSX.Element | null} the component to render, or null if no such component could be found
 */
export function PdfMarkdownSelectComponent({
	dropdownId,
}: Record<string, never>): ReactNode {
	return useDropdownData(dropdownId) ?? null
}

export const CLOZE_DROPDOWN_PDF_MARKDOWN_OVERRIDE = {
	[COMPONENT_NAME]: PdfMarkdownSelectComponent,
}

/**
 * MarkdownDropdownSelectComponent - a component used to render the correct button for the given dropdownId as described in the `ClozeDropdownProvider`.
 * Will be null if unable to find the component to render in the `ClozeDropdownProvider` state.
 *
 * @param {Object} props - the react props
 * @param {MdxJsxFlowElement | MdxJsxTextElement} props.node - the markdown html node containing the id of the dropdown button to render
 *
 * @return {JSX.Element | null} the component to render, or null if no such component could be found
 */
export function MarkdownDropdownSelectComponent({
	node,
}: {
	node: MdxJsxFlowElement | MdxJsxTextElement
}): ReactNode {
	const dropdownId = node.attributes.find(
		node => node.type === 'mdxJsxAttribute' && node.name === 'dropdownId'
	)?.value

	return useDropdownData(dropdownId) ?? null
}

/**
 * textToSpeechGetDropDown - text to render in place of the `MarkdownDropdownSelectComponent` when using text to speech.
 *
 * @param {MdxJsxFlowElement | MdxJsxTextElement} node - the markdown html node that corresponds to a dropdown
 *
 * @return {{ text: string } | null} - the text to render in place of the dropdown button, or null if the dropdown described in the `node` does not exist
 */
export function textToSpeechGetDropDown(
	node: MdxJsxFlowElement | MdxJsxTextElement
): { text: string } | null {
	const dropdownId = node.attributes.find(
		node => node.type === 'mdxJsxAttribute' && node.name === 'dropdownId'
	)?.value

	if (dropdownId == null) {
		return null
	}

	return {
		text: `dropdown ${dropdownId}`,
	}
}

/**
 * useTemplateMarkdownString - converts the template string into a markdown parsable string with the dropdown buttons in the correct position.
 * Returns null if the templateString was not supposed to be considered a markdown string. Should use `getMarkdownOverrides` if rendering as markdown
 * along with the `ClozeDropdownProvider`.
 *
 * @param {string} templateString - the template string
 *
 * @return {string | null} - the markdown string or null if the templateString should not be interpreted as markdown
 */
export function getTemplateMarkdownString(
	templateString: string
): string | null {
	const markdownString = getMarkdownString(templateString)
	if (markdownString == null) {
		return null
	}
	return parseTemplateStringIntoSegments(markdownString)
		.map(segment => {
			if (segment.type === TemplateSegmentType.Input) {
				return `<${COMPONENT_NAME} dropdownId="${segment.inputId}" />`
			}
			if (segment.type === TemplateSegmentType.Text) {
				return segment.text
			}
			return ''
		})
		.join('')
}

export const TEXT_TO_SPEECH_CLOSE_READING_MARKDOWN_OVERRIDES = {
	[COMPONENT_NAME]: {
		markdown: MarkdownDropdownSelectComponent,
		textToSpeech: textToSpeechGetDropDown,
	},
}
